<template>
  <nav
    :class="['navbar navbar-expand-lg py-3', { 'navbar-scrolled': isScrolled }]"
  >
    <div class="container">
      <div class="d-flex justify-content-between w-100 align-items-center">
        <!-- Logo -->
        <router-link
          v-if="!isScrolled && !isServicesPage"
          class="navbar-brand text-light"
          to="/"
        >
          <img src="/images/logoLight.svg" alt="logo" />
        </router-link>
        <router-link v-else class="navbar-brand text-dark" to="/">
          <img src="/images/logoDark.svg" alt="logo" />
        </router-link>

        <!-- Toggler -->
        <button
          class="navbar-toggler"
          type="button"
          ref="toggler"
          @click="sidebarOpen = true"
          aria-label="Toggle navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- Horizontal navbar -->
        <div class="horizontal-nav d-none d-lg-flex">
          <ul
            v-if="!isServicesPage"
            class="navbar-nav mb-2 mb-lg-0 align-items-center gap-3"
          >
            <li class="nav-item" v-for="link in navLinks" :key="link">
              <router-link
                :to="getLinkPath(link)"
                class="nav-link"
                :class="[
                  link === 'Get In Touch' ? 'btn-border' : '',
                  linkClasses(link),
                ]"
                @click.prevent="handleNavClick(link)"
              >
                {{ link }}
              </router-link>
            </li>
          </ul>
          <ul v-else class="navbar-nav mb-2 mb-lg-0 align-items-center gap-3">
            <li class="nav-item" v-for="link in navLinks" :key="link">
              <router-link
                v-if="route.path === '/services'"
                :to="getLinkPath(link)"
                class="nav-link"
                :class="[
                  link === 'Get In Touch' ? 'btn-border' : '',
                  linkClassesService(link),
                  link === 'Our Services' ? 'active-scrolled' : '',
                ]"
                @click.prevent="handleNavClick(link)"
              >
                {{ link }}
              </router-link>
              <router-link
                v-else
                :to="getLinkPath(link)"
                class="nav-link"
                :class="[
                  link === 'Get In Touch' ? 'btn-border' : '',
                  linkClassesService(link),
                  link === 'Global Presence' ? 'active-scrolled' : '',
                ]"
                @click.prevent="handleNavClick(link)"
              >
                {{ link }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Sidebar -->
        <div
          style="width: 300px"
          class="sidebar"
          :class="{ open: sidebarOpen }"
          ref="sidebar"
        >
          <button
            class="btn-close ms-auto mb-3"
            @click="sidebarOpen = false"
            aria-label="Close"
          ></button>
          <ul class="navbar-nav mb-2 mb-lg-0 align-items-start gap-3">
            <li class="nav-item" v-for="link in navLinks" :key="link">
              <router-link
                :to="getLinkPath(link)"
                class="nav-link text-light"
                :class="link === 'Get In Touch' ? 'btn-border' : ''"
                @click.prevent="handleNavClick(link)"
              >
                {{ link }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();

const isServicesPage = computed(
  () => route.path === "/services" || route.path === "/global-presence"
);
const isScrolled = ref(false);
const sidebarOpen = ref(false);
const sidebar = ref(null);
const toggler = ref(null);

const navLinks = [
  "Home",
  "About Us",
  "Our Services",
  "Global Presence",
  "Get In Touch",
];

const getLinkPath = (link) => {
  const paths = {
    Home: "/",
    "About Us": "/about",
    "Our Services": "/services",
    "Get In Touch": "/getTouch",
    "Global Presence": "/global-presence",
  };
  return paths[link] || "/";
};

// link classes
const linkClasses = (link) => {
  const isActive = route.path === getLinkPath(link);
  return {
    "link-default": !isScrolled.value,
    "link-scrolled": isScrolled.value,
    "active-default": isActive && !isScrolled.value,
    "active-scrolled": isActive && isScrolled.value,
  };
};
// link classes
const linkClassesService = (link) => {
  const isActive = route.path === getLinkPath(link);
  return {
    "active-default": !isScrolled.value,
    "active-scrolled": isScrolled.value,
    "active-default": isActive && !isScrolled.value,
    "active-scrolled": isActive && isScrolled.value,
  };
};

// nav click handler
const handleNavClick = (link) => {
  const targetPath = getLinkPath(link);
  if (route.path === targetPath) {
    // نفس الصفحة → scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    // صفحة مختلفة → redirect
    router.push(targetPath);
  }
  sidebarOpen.value = false; // يقفل الـ sidebar لو مفتوح
};

const toggleBodyScroll = () => {
  if (sidebarOpen.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

const handleClickOutside = (event) => {
  if (
    sidebarOpen.value &&
    sidebar.value &&
    !sidebar.value.contains(event.target) &&
    toggler.value &&
    !toggler.value.contains(event.target)
  ) {
    sidebarOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", handleScroll);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", handleScroll);
});
watch(sidebarOpen, toggleBodyScroll);
</script>

<style scoped>
/* Sidebar Styles */
.sidebar {
  position: fixed;
  top: 0;
  left: -100%;
  width: 250px;
  height: 100%;
  background: rgba(177, 32, 47, 1);
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.3);
  padding: 2rem 1rem;
  transition: left 0.3s ease;
  z-index: 1050;
  overflow-y: auto;
}

.sidebar.open {
  left: 0;
}

@media (min-width: 992px) {
  .sidebar {
    display: none;
  }
}

@media (max-width: 991px) {
  .horizontal-nav {
    display: none !important;
  }
}

/* الروابط */
.nav-link {
  transition: color 0.3s ease, font-weight 0.3s ease;
}

/* الحالة العادية */
.link-default {
  color: #e4e4e4 !important;
  font-weight: 500;
}

/* الرابط الحالي */
.active-default {
  color: #fff !important;
  font-weight: bold !important;
}

/* عند السكروول */
.link-scrolled {
  color: #212529 !important;
  font-weight: 500;
}

/* الرابط الحالي عند السكروول */
.active-scrolled {
  color: #b1202f !important;
  font-weight: bold !important;
}

/* زر Get In Touch */
.btn-border {
  border: 1px solid currentColor;
  padding: 12px 24px !important;
  border-radius: 4px;
}
</style>
